import { useState } from "react";
import { createPortal } from "react-dom";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Check, Loader2, UserRound } from "lucide-react";

/* Big, unmissable gender gate. Tick Male or Female, press Continue → saved to the profile, then onDone().
   onCancel is optional: when omitted (tournament flows) the only way forward is choosing. */
export default function GenderGateModal({ open, onDone, onCancel, reason }) {
  const { refresh } = useAuth();
  const [pick, setPick] = useState("");
  const [busy, setBusy] = useState(false);
  if (!open) return null;
  const save = async () => {
    if (!pick) return toast.error("Please tick Male or Female");
    setBusy(true);
    try {
      await api.put("/profile", { gender: pick });
      await refresh();
      toast.success("Saved — thanks!");
      onDone?.(pick);
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };
  return createPortal(
    <div className="fixed inset-0 z-[120] bg-black/85 backdrop-blur-sm flex items-center justify-center p-4" data-testid="gender-gate-modal">
      <div className="w-full max-w-[640px] bg-pb-surface border-2 border-pb-red shadow-[0_0_60px_rgba(239,68,68,0.35)] p-6 sm:p-10">
        <div className="flex items-center gap-3 text-pb-red">
          <UserRound className="w-8 h-8" />
          <span className="text-[11px] font-black uppercase tracking-[0.3em]">Required before you continue</span>
        </div>
        <h2 className="mt-4 font-display font-black uppercase tracking-tighter text-3xl sm:text-4xl text-white leading-none" data-testid="gender-gate-title">
          Select your gender
        </h2>
        <p className="mt-3 text-sm text-zinc-300 leading-relaxed" data-testid="gender-gate-text">
          {reason || "Tournament events are built as Men's, Women's and Mixed brackets, so we need your gender on file before you can join or sign up."} Tick one box below, then press Continue. You can change it any time in your Profile.
        </p>
        <div className="mt-6 grid grid-cols-2 gap-4">
          {[["male", "Male"], ["female", "Female"]].map(([v, label]) => {
            const on = pick === v;
            return (
              <button key={v} type="button" role="checkbox" aria-checked={on} data-testid={`gender-gate-${v}`} onClick={() => setPick(v)}
                className={`flex items-center gap-4 border-2 px-5 py-6 text-left transition-colors ${on ? "border-pb-cyan bg-pb-cyan/10" : "border-pb-border hover:border-zinc-400"}`}>
                <span className={`w-8 h-8 shrink-0 border-2 flex items-center justify-center ${on ? "border-pb-cyan bg-pb-cyan text-black" : "border-zinc-500"}`}>
                  {on && <Check className="w-6 h-6" strokeWidth={3} />}
                </span>
                <span className="font-display font-black uppercase tracking-tight text-2xl text-white">{label}</span>
              </button>
            );
          })}
        </div>
        <div className="mt-8 flex items-center justify-between gap-3 flex-wrap">
          {onCancel ? (
            <button type="button" data-testid="gender-gate-later" onClick={onCancel} className="text-[11px] uppercase tracking-widest text-zinc-500 hover:text-white">Not now</button>
          ) : <span />}
          <button type="button" data-testid="gender-gate-continue" onClick={save} disabled={busy || !pick}
            className="inline-flex items-center gap-2 bg-pb-red text-black px-8 py-3 text-xs font-black uppercase tracking-[0.25em] hover:bg-white disabled:opacity-40">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : null} Continue
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}
